import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * A list's state (tab, page, page size, search, filters) kept in the URL
 * query, so it survives opening a row and coming Back, a refresh, or a shared
 * link. `defaults` maps every key to its default text and must be a module
 * constant (a new object each render would re-run every effect on `params`).
 *
 * `params` holds every key as text, the default when the URL has none.
 * `update({ key: value })` writes the given keys; a key set to its default,
 * "", null or undefined leaves the URL. Updates replace the history entry, so
 * Back leaves the list instead of stepping through its pages.
 */
const useListParams = (defaults) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => {
    const values = {};
    Object.keys(defaults).forEach((key) => {
      values[key] = searchParams.get(key) ?? defaults[key];
    });
    return values;
  }, [searchParams, defaults]);

  const update = useCallback(
    (changes) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          Object.entries(changes).forEach(([key, value]) => {
            const text = value == null ? "" : String(value);
            if (text === "" || text === String(defaults[key] ?? "")) {
              next.delete(key);
            } else {
              next.set(key, text);
            }
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, defaults]
  );

  return [params, update];
};

/** A 1-based page number from URL text; 1 when it is not a positive integer. */
export const toPage = (value) => {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page : 1;
};

/** A page size from URL text, kept to `options`; `fallback` otherwise. */
export const toPageSize = (value, options, fallback) => {
  const size = Number(value);
  return options.includes(size) ? size : fallback;
};

export default useListParams;
